import { runApplyCPQSelections } from "./cpq-apply-selections";
import {
  listCpqLinkedDisplayAttributesFromConfigurator,
  type ApplyCPQSelectionsOptions,
  type CameraSnapshotSpec,
  type CustomEventParams,
  type OptionInteractionParams,
  type OptionsShowParams,
  type SnapshotAndUploadOptions,
  type ThreekitEmbedHandle,
} from "./embed-api";
import type { OptionsShowAllFromConfiguratorOptions } from "./options-show-all-attributes";

export interface CpqPostMessageBridgeOptions {
  isOriginAllowed(origin: string): boolean;
  /** Called after `destroy` from a `THREEKIT_CPQ_DESTROY` message (e.g. allow remount). */
  onTeardown?(): void;
}

export interface CpqPostMessageBridge {
  detach(): void;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function reply(ev: MessageEvent, payload: Record<string, unknown>): void {
  const src = ev.source;
  if (src && typeof (src as Window).postMessage === "function") {
    (src as Window).postMessage(payload, ev.origin);
  }
}

function sendResult(
  ev: MessageEvent,
  requestId: string | undefined,
  ok: boolean,
  extra?: Record<string, unknown>
): void {
  if (requestId === undefined) return;
  reply(ev, { type: "THREEKIT_CPQ_RESULT", requestId, ok, ...extra });
}

function coerceSelections(
  raw: unknown
): Record<string, string | number> | null {
  if (!isRecord(raw)) return null;
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v === "string" || typeof v === "number") out[k] = v;
  }
  return out;
}

function coerceCameraSpecs(raw: unknown): CameraSnapshotSpec[] | null {
  if (!Array.isArray(raw)) return null;
  const out: CameraSnapshotSpec[] = [];
  for (const entry of raw) {
    if (typeof entry === "string" && entry.trim()) {
      out.push(entry);
    } else if (isRecord(entry) && typeof entry.cameraName === "string" && entry.cameraName.trim()) {
      out.push(entry as unknown as CameraSnapshotSpec);
    }
  }
  return out;
}

/**
 * Listens for `THREEKIT_CPQ_*` messages from an allowed parent window and forwards them to
 * a {@link ThreekitEmbedHandle}. Use after `mount()` inside an iframe host page.
 *
 * Inbound (parent → iframe), besides `THREEKIT_CPQ_MOUNT` (handled by the host before a handle exists):
 * - `THREEKIT_CPQ_SET_CONFIGURATION` — `{ configuration }`, optional `requestId`
 * - `THREEKIT_CPQ_OPTIONS_SHOW` — `{ params }`, optional `requestId`
 * - `THREEKIT_CPQ_OPTION_INTERACTION` — `{ params }`, optional `requestId`
 * - `THREEKIT_CPQ_CUSTOM` — `{ params }`, optional `requestId`
 * - `THREEKIT_CPQ_OPTIONS_SHOW_ALL_FROM_CONFIGURATOR` — optional `{ options }`, optional `requestId`
 * - `THREEKIT_CPQ_APPLY_SELECTIONS` — `{ selections, options? }`, optional `requestId` (fires when apply finishes)
 * - `THREEKIT_CPQ_RUN_APPLY_SELECTIONS` — `{ selections, options? }`, optional `requestId` (Promise-based)
 * - `THREEKIT_CPQ_GET_LINKED_DISPLAY_ATTRIBUTES` — `{ requestId }` required; `THREEKIT_CPQ_RESULT.result` is
 *   `CpqLinkedDisplayAttribute[]` for the parent to pair with `CPQJS.getAttributeVal` then `THREEKIT_CPQ_APPLY_SELECTIONS`
 * - `THREEKIT_CPQ_SNAPSHOT_AND_UPLOAD` — optional `{ options?: SnapshotAndUploadOptions }`, `requestId` recommended;
 *   result is `{ url, file, raw }` (https URL for CPQ)
 * - `THREEKIT_CPQ_SNAPSHOT_CAMERAS_AND_UPLOAD` — `{ cameras: CameraSnapshotSpec[], options?: SnapshotAndUploadOptions }`,
 *   `requestId` required; result is `Record<cameraName, { url, file, raw }>`, one entry per resolved camera
 *   (each name is resolved to a scene node id via `player.scene.findNode({ name })`)
 * - `THREEKIT_CPQ_SNAPSHOT_STANDARD_CAMERAS_AND_UPLOAD` — optional `{ options?: SnapshotAndUploadOptions }`,
 *   `requestId` required; same as above but with this product's 3 cameras hardcoded — result is
 *   `{ main: { url, file, raw }, front: {...}, side: {...} }`
 * - `THREEKIT_CPQ_RESOLVE_CAMERA_ID` — `{ cameraName: string }`, `requestId` required; result is the
 *   camera's scene node id (string), resolved via `player.scene.findNode({ name, hierarchical: true })`
 * - `THREEKIT_CPQ_DESTROY` — optional `requestId`; detaches this bridge, calls `handle.destroy()`, then `onTeardown`
 *
 * Outbound (iframe → parent): `THREEKIT_CPQ_RESULT` with `{ requestId, ok, result? | message? }` when `requestId` was sent.
 */
export function attachCpqPostMessageBridge(
  handle: ThreekitEmbedHandle,
  options: CpqPostMessageBridgeOptions
): CpqPostMessageBridge {
  const { isOriginAllowed, onTeardown } = options;

  const onMessage = (ev: MessageEvent): void => {
    if (!isOriginAllowed(ev.origin)) return;
    const raw = ev.data;
    if (!isRecord(raw)) return;
    const type = raw.type;
    if (typeof type !== "string" || !type.startsWith("THREEKIT_CPQ_")) return;
    if (type === "THREEKIT_CPQ_MOUNT") return;

    const requestId =
      typeof raw.requestId === "string" ? raw.requestId : undefined;

    const fail = (message: string): void => {
      sendResult(ev, requestId, false, { message });
    };

    switch (type) {
      case "THREEKIT_CPQ_SET_CONFIGURATION": {
        void handle
          .setConfiguration(raw.configuration)
          .then((value) => {
            sendResult(ev, requestId, true, { result: value });
          })
          .catch((err: unknown) => {
            fail(err instanceof Error ? err.message : String(err));
          });
        break;
      }
      case "THREEKIT_CPQ_OPTIONS_SHOW": {
        try {
          handle.optionsShow(raw.params as OptionsShowParams);
          sendResult(ev, requestId, true, {});
        } catch (err: unknown) {
          fail(err instanceof Error ? err.message : String(err));
        }
        break;
      }
      case "THREEKIT_CPQ_OPTION_INTERACTION": {
        try {
          handle.optionInteraction(raw.params as OptionInteractionParams);
          sendResult(ev, requestId, true, {});
        } catch (err: unknown) {
          fail(err instanceof Error ? err.message : String(err));
        }
        break;
      }
      case "THREEKIT_CPQ_CUSTOM": {
        try {
          handle.custom(raw.params as CustomEventParams);
          sendResult(ev, requestId, true, {});
        } catch (err: unknown) {
          fail(err instanceof Error ? err.message : String(err));
        }
        break;
      }
      case "THREEKIT_CPQ_OPTIONS_SHOW_ALL_FROM_CONFIGURATOR": {
        try {
          const bridgeOpts = raw.options as
            | OptionsShowAllFromConfiguratorOptions
            | undefined;
          handle.optionsShowAllFromConfigurator(bridgeOpts);
          sendResult(ev, requestId, true, {});
        } catch (err: unknown) {
          fail(err instanceof Error ? err.message : String(err));
        }
        break;
      }
      case "THREEKIT_CPQ_APPLY_SELECTIONS": {
        const selections = coerceSelections(raw.selections);
        if (!selections || Object.keys(selections).length === 0) {
          fail("THREEKIT_CPQ_APPLY_SELECTIONS: expected non-empty selections object");
          break;
        }
        const baseOpts: ApplyCPQSelectionsOptions = isRecord(raw.options)
          ? { ...(raw.options as ApplyCPQSelectionsOptions) }
          : {};
        if (requestId !== undefined) {
          const userOnComplete = baseOpts.onComplete;
          baseOpts.onComplete = (error, result) => {
            userOnComplete?.(error, result);
            if (error) {
              sendResult(ev, requestId, false, { message: error.message });
            } else {
              sendResult(ev, requestId, true, { result });
            }
          };
        }
        try {
          handle.applyCPQSelections(selections, baseOpts);
        } catch (err: unknown) {
          fail(err instanceof Error ? err.message : String(err));
        }
        break;
      }
      case "THREEKIT_CPQ_RUN_APPLY_SELECTIONS": {
        const selections = coerceSelections(raw.selections);
        if (!selections || Object.keys(selections).length === 0) {
          fail("THREEKIT_CPQ_RUN_APPLY_SELECTIONS: expected non-empty selections object");
          break;
        }
        const applyOpts = isRecord(raw.options)
          ? (raw.options as ApplyCPQSelectionsOptions)
          : undefined;
        void runApplyCPQSelections(handle, selections, applyOpts)
          .then((result) => {
            sendResult(ev, requestId, true, { result });
          })
          .catch((err: unknown) => {
            fail(err instanceof Error ? err.message : String(err));
          });
        break;
      }
      case "THREEKIT_CPQ_GET_LINKED_DISPLAY_ATTRIBUTES": {
        if (requestId === undefined) {
          console.warn(
            "[Threekit CPQ bridge] THREEKIT_CPQ_GET_LINKED_DISPLAY_ATTRIBUTES ignored: pass requestId"
          );
          break;
        }
        try {
          const linked = listCpqLinkedDisplayAttributesFromConfigurator(handle.configurator);
          sendResult(ev, requestId, true, { result: linked });
        } catch (err: unknown) {
          fail(err instanceof Error ? err.message : String(err));
        }
        break;
      }
      case "THREEKIT_CPQ_SNAPSHOT_AND_UPLOAD": {
        if (requestId === undefined) {
          console.warn(
            "[Threekit CPQ bridge] THREEKIT_CPQ_SNAPSHOT_AND_UPLOAD ignored: pass requestId to receive { url, file, raw }"
          );
          break;
        }
        const snapOpts = isRecord(raw.options)
          ? (raw.options as SnapshotAndUploadOptions)
          : undefined;
        void handle
          .snapshotAndUploadConfiguration(snapOpts)
          .then((result) => {
            sendResult(ev, requestId, true, { result });
          })
          .catch((err: unknown) => {
            fail(err instanceof Error ? err.message : String(err));
          });
        break;
      }
      case "THREEKIT_CPQ_SNAPSHOT_CAMERAS_AND_UPLOAD": {
        if (requestId === undefined) {
          console.warn(
            "[Threekit CPQ bridge] THREEKIT_CPQ_SNAPSHOT_CAMERAS_AND_UPLOAD ignored: pass requestId to receive the per-camera result"
          );
          break;
        }
        const cameras = coerceCameraSpecs(raw.cameras);
        if (!cameras || cameras.length === 0) {
          fail(
            "THREEKIT_CPQ_SNAPSHOT_CAMERAS_AND_UPLOAD: expected non-empty cameras array of names or { cameraName } specs"
          );
          break;
        }
        const snapOpts = isRecord(raw.options)
          ? (raw.options as SnapshotAndUploadOptions)
          : undefined;
        void handle
          .snapshotCamerasAndUpload(cameras, snapOpts)
          .then((result) => {
            sendResult(ev, requestId, true, { result });
          })
          .catch((err: unknown) => {
            fail(err instanceof Error ? err.message : String(err));
          });
        break;
      }
      case "THREEKIT_CPQ_SNAPSHOT_STANDARD_CAMERAS_AND_UPLOAD": {
        if (requestId === undefined) {
          console.warn(
            "[Threekit CPQ bridge] THREEKIT_CPQ_SNAPSHOT_STANDARD_CAMERAS_AND_UPLOAD ignored: pass requestId to receive { main, front, side }"
          );
          break;
        }
        const snapOpts = isRecord(raw.options)
          ? (raw.options as SnapshotAndUploadOptions)
          : undefined;
        void handle
          .snapshotStandardCamerasAndUpload(snapOpts)
          .then((result) => {
            sendResult(ev, requestId, true, { result });
          })
          .catch((err: unknown) => {
            fail(err instanceof Error ? err.message : String(err));
          });
        break;
      }
      case "THREEKIT_CPQ_RESOLVE_CAMERA_ID": {
        if (requestId === undefined) {
          console.warn(
            "[Threekit CPQ bridge] THREEKIT_CPQ_RESOLVE_CAMERA_ID ignored: pass requestId to receive the camera id"
          );
          break;
        }
        const cameraName = raw.cameraName;
        if (typeof cameraName !== "string" || !cameraName.trim()) {
          fail("THREEKIT_CPQ_RESOLVE_CAMERA_ID: expected non-empty cameraName string");
          break;
        }
        try {
          sendResult(ev, requestId, true, { result: handle.resolveCameraId(cameraName) });
        } catch (err: unknown) {
          fail(err instanceof Error ? err.message : String(err));
        }
        break;
      }
      case "THREEKIT_CPQ_DESTROY": {
        try {
          window.removeEventListener("message", onMessage);
          handle.destroy();
          sendResult(ev, requestId, true, {});
          onTeardown?.();
        } catch (err: unknown) {
          fail(err instanceof Error ? err.message : String(err));
        }
        break;
      }
      default:
        break;
    }
  };

  window.addEventListener("message", onMessage);

  return {
    detach(): void {
      window.removeEventListener("message", onMessage);
    },
  };
}
